import { useContext, useRef, useState } from 'react';
import uniqueId from 'lodash/uniqueId';
import useTranslation from 'next-translate/useTranslation';
import { getRegionFullLabelByHouseNr } from 'helpers/museumMap';
import getHeadingSkin from 'helpers/getHeadingSkin';
import getHeadingTag from 'helpers/getHeadingTag';
import { formatDateToDisplay } from 'helpers/date';
import Picture from 'components/atoms/Picture/Picture';
import { useLinkResolver } from 'prismic/helpers/useLinkResolver';
import { BreakpointContext } from 'contexts/breakpoint/BreakpointContext';
import { EventBoxBigProps } from './EventBoxBig.types';
import { BoxMapCursor } from './BoxMapCursor.partial';
import { houses } from './EventBoxBig.helper';
import {
  DescriptionBoxStyled,
  IconMapStyled,
  IconStyled,
  IconWrapperStyled,
  ImageStyled,
  SvgImageStyled,
  InfoBoxStyled,
  InfoRowMapStyled,
  InfoRowStyled,
  RichTextStyled,
  SubHeadlineStyled,
  Wrapper,
  RowContentStyled,
  RowMapStyled,
  LinkToMapStyled,
  DateStyled,
  HeadlineStyled,
  SeparatorStyled,
} from './EventBoxBig.styles';
import { useRouter } from 'next/router';

export const EventBoxBig: React.FC<EventBoxBigProps> = ({ className, data }) => {
  const { t, lang } = useTranslation('common');
  const { locale, documentRelations, linkResolver } = useLinkResolver();
  const bp = useContext(BreakpointContext);
  const isMobile = bp === 'xs' || bp === 'sm';
  const router = useRouter();
  const [showMap, setShowMap] = useState<boolean>(false);
  const ref = useRef(`box-map-${uniqueId()}`);
  const svgBackground = {
    width: 1000,
    height: 350,
  };

  const getLinkFromHouseEvent = (): string | undefined => {
    if (data._linkType && data._meta.uid) {
      const urlToLinkResolver = {
        link_type: data._linkType,
        uid: 'museumsplan',
        lang: locale,
      };

      const href = linkResolver(urlToLinkResolver, documentRelations);
      return `${href}?h=${data.termin?.location?.house_nr}`;
    }
  };

  const formatDate = (dateStr: string | undefined | null) => {
    if (!dateStr) {
      return '';
    }

    return formatDateToDisplay(new Date(dateStr));
  };

  function LinkToEvent(data: any) {
    if (data._type === 'event') {
      console.log(true);
      router.push(`/${lang}/events/${data._meta.uid}`, undefined);
    }
  }

  return (
    <Wrapper className={className}>
      <RowContentStyled isEvent={data._type === 'event'} onClick={() => LinkToEvent(data)}>
        <ImageStyled className={showMap ? 'isOpen' : ''}>
          {data.image && data.image.dimensions && (
            <Picture noMinHeight image={data.image} objectFit></Picture>
          )}
        </ImageStyled>

        <DescriptionBoxStyled>
          <HeadlineStyled skin={getHeadingSkin('heading3')} elementType={getHeadingTag('heading3')}>
            {data.display_name}
          </HeadlineStyled>
          {data.category && <SubHeadlineStyled>{t(data.category)}</SubHeadlineStyled>}
          <RichTextStyled rich_text={data.description} />
        </DescriptionBoxStyled>
        <SeparatorStyled />
        <InfoBoxStyled>
          <InfoRowStyled>
            <IconWrapperStyled>
              <IconStyled name="CALENDAR" />
            </IconWrapperStyled>
            <DateStyled>{formatDate(data.termin?.date)}</DateStyled>
          </InfoRowStyled>
          {data.termin?.timeslot && (
            <InfoRowStyled>
              <IconWrapperStyled>
                <IconStyled name="CLOCK" />
              </IconWrapperStyled>
              <div>{data.termin?.timeslot || ''}</div>
            </InfoRowStyled>
          )}
          {data.termin?.location?.name && (
            <>
              {!isMobile ? (
                <InfoRowStyled>
                  <IconWrapperStyled>
                    <IconStyled name="MAPCURSOR" />
                  </IconWrapperStyled>
                  <div>{data.termin.location.house_nr ? `${data.termin.location.house_nr} ${data.termin.location.name}` : data.termin.location.name}</div>
                </InfoRowStyled>
              ) : data.termin.location.house_nr ? (
                <InfoRowStyled>
                  <LinkToMapStyled href={getLinkFromHouseEvent()}>
                    <IconWrapperStyled>
                      <IconStyled name="MAPCURSOR" />
                    </IconWrapperStyled>
                    {`${data.termin.location.house_nr} ${data.termin.location.name}`}
                  </LinkToMapStyled>
                </InfoRowStyled>
              ) : (
                <InfoRowStyled>
                  <IconWrapperStyled>
                    <IconStyled name="MAPCURSOR" />
                  </IconWrapperStyled>
                  <div>{data.termin.location.name}</div>
                </InfoRowStyled>
              )}
              {data.termin.location.ort && (
                <InfoRowStyled>
                  <IconWrapperStyled />
                  <div>{data.termin.location.ort}</div>
                </InfoRowStyled>
              )}

              {!isMobile && data.termin.location.house_nr && (
                <InfoRowMapStyled
                  onClick={(e) => {
                    e.preventDefault();
                    setShowMap((current: boolean) => !current);
                    setTimeout(() => {
                      const cursor = document.getElementById(`${ref.current}_locationCursor`);
                      if (cursor) {
                        const selectedHouse = houses.find((house) => {
                          if (data.termin?.location?.house_nr) {
                            return house.house_nr === data.termin.location.house_nr;
                          }
                          return false;
                        });
                        if (selectedHouse) {
                          const x = selectedHouse.x;
                          const y = selectedHouse.y;
                          let translate = `${x - 55} ${y - 50}`;

                          const transform = `translate(${translate})`;
                          cursor.setAttribute('transform', transform);
                          cursor.style.display = 'block';
                        } else {
                          cursor.style.display = 'none';
                        }
                      }
                    }, 100);
                  }}
                >
                  <IconWrapperStyled>
                    <IconMapStyled name={showMap ? 'CLOSE' : 'MAP_PLAN'} />
                  </IconWrapperStyled>
                  {!showMap ? t('showMap') : t('hiddeMap')}
                </InfoRowMapStyled>
              )}
            </>
          )}
        </InfoBoxStyled>
      </RowContentStyled>
      {!isMobile && showMap && (
        <RowMapStyled>
          <svg
            viewBox={`0 0 ${svgBackground.width} ${svgBackground.height}`}
            id={ref.current}
            width={'100%'}
            height={'100%'}
          >
            <SvgImageStyled
              href="/map/ballenberg_abstrakte_karte_v1.jpg"
              x="0"
              y="0"
              height={svgBackground.height}
              width={svgBackground.width}
            />
            <BoxMapCursor
              mapId={ref.current}
              text={`${data?.termin?.location?.house_nr || ''}, ${
                data?.termin?.location?.name || ''
              }`}
              subtext={t(getRegionFullLabelByHouseNr(data.termin?.location?.house_nr))}
              link={getLinkFromHouseEvent()}
            />
          </svg>
        </RowMapStyled>
      )}
    </Wrapper>
  );
};
